import { useEffect, useMemo, useRef, useState } from 'react';
import { EVENT_CATEGORIES, CATEGORY_GROUPS } from '../constants/eventCategories';
import { fillFor } from './wall/wallTokens';

/**
 * CategorySelect — type-to-find picker for the event categories.
 *
 * There are 49 of them. Laid out as chips they wrapped to five rows and pushed
 * the wall itself off the screen, and finding "Karaoke" meant reading all 49.
 * Typing two letters is faster than scanning, so search leads and the list is
 * grouped underneath — the groups are what make a long list skimmable when you
 * don't yet know what you're looking for.
 *
 * Still multi-select, and still the same comma-separated slug string the URL and
 * the server already speak. What's selected sits at the top as removable chips,
 * so the current filter stays visible while you search for the next one instead
 * of being lost somewhere in a list you've scrolled past.
 */
export default function CategorySelect({
  selected = new Set(),
  onToggle,
  onClear,
  /** Include the "Other" bucket. Off for public browsing; on for curation,
   *  where Other is ~45% of live events and has to be reachable. */
  includeOther = false,
  /** 'list' (default, bordered rows) · 'pills' — the events wall's FILTERS drawer, where the
   *  same type-to-find picker lays its options out as ring-bordered pill chips. */
  variant = 'list',
  /** Pills only: focus the search box on mount. The drawer keeps a closing panel rendered, so
   *  it asks for focus only when the panel is actually opening. */
  autoFocus = true,
}) {
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // The panel opens straight into the search box — that's the whole point of it.
  useEffect(() => { if (autoFocus) inputRef.current?.focus(); }, [autoFocus]);

  // Grouped first, alphabetical within the group. The source array is authored
  // in group order but arbitrary order inside each one, so "Karaoke" could sit
  // above "Dance" for no reason a reader could see.
  const options = useMemo(() => {
    const pool = includeOther ? EVENT_CATEGORIES : EVENT_CATEGORIES.filter((c) => c.slug !== 'other');
    const groupRank = (c) => {
      const i = CATEGORY_GROUPS.indexOf(c.group);
      return i === -1 ? CATEGORY_GROUPS.length : i;
    };
    return [...pool].sort((a, b) => groupRank(a) - groupRank(b) || a.label.localeCompare(b.label));
  }, [includeOther]);

  // Match on label and on group, so "music" also surfaces everything filed under
  // Music & Nightlife. Ranked: what starts with the query beats what merely
  // contains it, which puts "Art" above "Performing Arts" when you type "art".
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    const scored = [];
    for (const c of options) {
      const label = c.label.toLowerCase();
      const group = (c.group || '').toLowerCase();
      let score = -1;
      if (label.startsWith(q)) score = 0;
      else if (label.includes(q)) score = 1;
      else if (group.includes(q)) score = 2;
      if (score >= 0) scored.push({ c, score });
    }
    return scored.sort((a, b) => a.score - b.score || a.c.label.localeCompare(b.c.label)).map((s) => s.c);
  }, [options, query]);

  // Keep the highlight in range as the list narrows under you.
  useEffect(() => { setCursor(0); }, [query]);

  // Group headers only make sense while browsing; a search result is already
  // ordered by relevance, and headers would fight that ordering.
  const grouped = useMemo(() => {
    if (query.trim()) return [{ group: null, items: matches }];
    const out = [];
    for (const c of matches) {
      const last = out[out.length - 1];
      if (last && last.group === c.group) last.items.push(c);
      else out.push({ group: c.group, items: [c] });
    }
    return out;
  }, [matches, query]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((i) => {
        const next = e.key === 'ArrowDown' ? i + 1 : i - 1;
        return Math.max(0, Math.min(matches.length - 1, next));
      });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = matches[cursor];
      if (pick) { onToggle?.(pick.slug); setQuery(''); }
    }
  };

  // Follow the keyboard cursor when it walks past the visible window.
  useEffect(() => {
    const row = listRef.current?.querySelector('[data-cursor="true"]');
    // Not every environment implements scrollIntoView (jsdom doesn't); a missing
    // scroll is cosmetic, so never let it take the component down.
    row?.scrollIntoView?.({ block: 'nearest' });
  }, [cursor]);

  const selectedList = options.filter((c) => selected.has(c.slug));

  if (variant === 'pills') {
    const pill = (c, on, at) => (
      <button
        key={c.slug}
        type="button"
        data-cursor={at ? 'true' : undefined}
        onClick={() => onToggle?.(c.slug)}
        aria-pressed={on}
        className="whitespace-nowrap rounded-full font-mono font-bold uppercase transition-colors duration-200"
        style={{
          padding: '7px 14px', fontSize: 10, letterSpacing: '0.06em', border: '2px solid transparent',
          background: fillFor(on ? 'on' : at ? 'open' : 'off'), color: on ? '#fff' : '#000',
        }}
      >
        {c.label}
      </button>
    );
    return (
      <div>
        <div className="flex items-center gap-3">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search categories…"
            aria-label="Search categories"
            className="h-9 min-w-0 flex-1 rounded-full px-4 font-mono text-[12px] text-black placeholder-hand-muted focus:outline-none"
            style={{ border: '2px solid transparent', background: fillFor(query ? 'open' : 'off') }}
          />
          {selectedList.length > 0 && (
            <button type="button" onClick={() => onClear?.()} className="flex-shrink-0 font-mono text-[11px] tracking-[0.1em] text-black underline hover:no-underline">
              CLEAR
            </button>
          )}
        </div>
        <div ref={listRef} className="mt-3 max-h-56 overflow-y-auto">
          {matches.length === 0 ? (
            <p className="py-3 font-mono text-[11px] uppercase tracking-wide text-hand-muted">Nothing matches “{query.trim()}”</p>
          ) : grouped.map((section) => (
            <div key={section.group || 'results'} className="mb-3 last:mb-0">
              {section.group && (
                <div className="mb-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-hand-muted">{section.group}</div>
              )}
              <div className="flex flex-wrap gap-2">
                {section.items.map((c) => pill(c, selected.has(c.slug), !!query.trim() && matches.indexOf(c) === cursor))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* What's on right now — visible while you hunt for the next one. */}
      {selectedList.length > 0 && (
        <div className="mb-2 flex flex-wrap items-center gap-1.5">
          {selectedList.map((c) => (
            <button
              key={c.slug}
              type="button"
              onClick={() => onToggle?.(c.slug)}
              aria-label={`Remove ${c.label}`}
              className="flex items-center gap-1.5 border-2 border-black bg-black px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wide text-white hover:bg-white hover:text-black"
            >
              {c.label}
              <span aria-hidden className="text-[11px] leading-none">×</span>
            </button>
          ))}
          <button
            type="button"
            onClick={() => onClear?.()}
            className="font-mono text-[10px] font-bold uppercase tracking-wide underline hover:no-underline"
          >
            Clear
          </button>
        </div>
      )}

      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder="Search categories…"
        aria-label="Search categories"
        className="w-full border-2 border-black bg-white px-3 py-2 font-mono text-xs text-black placeholder-gray-400 focus:outline-none"
      />

      <div ref={listRef} className="mt-2 max-h-64 overflow-y-auto border-2 border-black">
        {matches.length === 0 ? (
          <p className="px-3 py-4 text-center font-mono text-[11px] uppercase tracking-wide text-gray-500">
            Nothing matches “{query.trim()}”
          </p>
        ) : (
          grouped.map((section) => (
            <div key={section.group || 'results'}>
              {section.group && (
                <div className="sticky top-0 border-b border-gray-200 bg-gray-50 px-3 py-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-gray-500">
                  {section.group}
                </div>
              )}
              {section.items.map((c) => {
                const on = selected.has(c.slug);
                const at = matches.indexOf(c) === cursor;
                return (
                  <button
                    key={c.slug}
                    type="button"
                    data-cursor={at ? 'true' : undefined}
                    onClick={() => onToggle?.(c.slug)}
                    aria-pressed={on}
                    className={`flex w-full items-center justify-between gap-3 border-b border-gray-200 px-3 py-2 text-left font-mono text-[11px] font-bold uppercase tracking-wide transition-colors ${
                      on ? 'bg-black text-white' : at ? 'bg-gray-100 text-black' : 'bg-white text-black hover:bg-gray-100'
                    }`}
                  >
                    <span className="truncate">{c.label}</span>
                    {on && <span aria-hidden className="text-[11px] leading-none">✓</span>}
                  </button>
                );
              })}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
